import { useContext, useRef, useState } from "react";
import Backimg from "../../assets/img/backicon01.svg";
import UserImg2 from "../../assets/img/user-account2.svg";
import Arrow from "../../assets/img/arrow-left-02.svg";
import lockIcon from "../../assets/img/lockIcon.svg";
import useTranslator from "../../hooks/useTranslator";
import TenantContext from "../../context/tenant/TenantContext";
import axiosInstance from "../../services/authWrapper";
import { Onfido } from "onfido-sdk-ui";
import AuthContext from "../../context/auth/AuthContext";
import SkorDefaultLoader from "../../assets/img/skorDefaultLoader.gif";
import KycVerificationPopup from "./KycVerificationPopup";
import { KycDocLoader } from "../../components/Onboarding/KycDocLoader";
import ProcessFailedModal from "./ProcessFailedModal";
import { useNavigate } from "react-router-dom";
import Cancelimg from "../../assets/img/cancel-01.svg";
import { trackClarityEvent } from "../../Utils/clarityReady";
import { toast } from "../../helper/common";
import constants from "../../Utils/constants";

const AuthenticateYourId = ({
  user,
  formHandler,
  updateUserProfile,
  isGuarantor,
  setRunDetectKycDocs,
  usedFor,
  setShowKycPopup,
  setShowDocScanningStep,
  addedFiles,
  setAddedFiles,
  getNextStep,
}) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const { loadUser, loadUserTenant } = useContext(AuthContext);
  const { onfidoKyc, uploadFileData } = useContext(TenantContext);
  const [isDragging, setIsDragging] = useState(false);

  const [failedKyc, setFailedKyc] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef(null);
  const [docUploadLoading, setDocUploadLoading] = useState(false);

  const allowedTypes = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/jpg",
  ];

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const processFiles = (newFiles) => {
    if (!Array.isArray(newFiles)) return;

    const totalFiles = newFiles.length;

    if (totalFiles > 10) {
      toast.error(_t("maxTenDocumentsAreAllowed"));
      return;
    }

    handleUploadFiles(newFiles);
  };

  const handleUploadFiles = async (
    allDocs,
    fileType = "identity-doc-from-onboarding",
    fileFrom
  ) => {
    if (allDocs.length === 0) {
      toast.error(
        _t("pleaseUploadDocumentsToContinue", "Text used in onboarding flow")
      );
      return;
    }
    try {
      setRunDetectKycDocs(true);
      if (usedFor === "popup") {
        setShowDocScanningStep(true);
      } else {
        formHandler.next();
      }
      setDocUploadLoading(true);
      const formData = new FormData();

      for (let file of allDocs) {
        formData.append("tenantDocs", file);
      }

      formData.append("fileType", fileType);
      if (fileFrom) formData.append("fileFrom", fileFrom);

      await uploadFileData(formData, false, user?._id, isGuarantor);
      if (usedFor === "popup") {
        await updateUserProfile({
          role: "tenant",
          manual_kyc_doc_status: "PROCESSING",
        });
        setShowDocScanningStep(true);
      } else {
        setAddedFiles([]);
        await updateUserProfile({
          step: 5,
          role: "tenant",
          manual_kyc_doc_status: "PROCESSING",
        });
      }
    } catch (err) {
      console.error("Error uploading multiple files:", err);
    } finally {
      setDocUploadLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const validFiles = Array.from(files).filter((file) =>
        allowedTypes.includes(file.type)
      );

      if (validFiles.length > 0) {
        // processFiles(validFiles);
        setAddedFiles((prev) => [...prev, ...validFiles]);
        // console.log("Files", validFiles);
      } else {
        toast.error(
          _t(
            "onlyPdfJpgAndPngFilesAreAllowed",
            "Text used as a error message in document upload"
          )
        );
      }
    }
    e.target.value = "";
  };

  const submitVerification = () => {
    processFiles(addedFiles);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const validFiles = Array.from(files).filter((file) =>
        allowedTypes.includes(file.type)
      );

      if (validFiles.length > 0) {
        // processFiles(validFiles);
        setAddedFiles((prev) => [...prev, ...validFiles]);
        console.log("files", validFiles);
      } else {
        toast.error(
          _t(
            "onlyPdfJpgAndPngFilesAreAllowed",
            "Text used as a error message in document upload"
          )
        );
      }
    }
  };

  // ----------------------------- onfido functionality -----------------------------
  const [onfidoStarted, setOnfidoStarted] = useState(false);
  const [onfidoInitiateLoading, setOnfidoInitiateLoading] = useState(false);
  const [kycVerificationPopup, setKycVerificationPopup] = useState(false);
  const [showOnfidoWatingLoading, setShowOnfidoWatingLoading] = useState(false);
  const [showKycLoading, setShowKycLoading] = useState(false);

  const pollKycStatus = (userId, onSuccess) => {
    const interval = setInterval(async () => {
      try {
        const res = await axiosInstance.get(
          `${process.env.REACT_APP_SERVER_URL}/tenants/getKycStatusInfo?userId=${user?._id}`
        );
        if (res?.data?.status == "completed") {
          clearInterval(interval);
          onSuccess && onSuccess();
        } else if (res?.data?.status == "error") {
          setShowOnfidoWatingLoading(false);
          setShowKycLoading(false);
          setFailedKyc(true);
          clearInterval(interval);
        }
      } catch (error) {
        console.error("Polling kyc status error:", error);
        clearInterval(interval);
      }
    }, 5000);
  };

  const initiateOnfido = () => {
    setAddedFiles([]);
    setOnfidoInitiateLoading(true);

    const baseUrl = `${process.env.REACT_APP_SERVER_URL}/tenants/kyc/${user?._id}`;
    const queryParams = [];
    if (isGuarantor) queryParams.push("guarantor=true");
    if (usedFor === "tenantChecks") queryParams.push("llCreated=true");

    const url = `${baseUrl}${
      queryParams.length ? "?" + queryParams.join("&") : ""
    }`;

    axiosInstance
      .get(url)
      .then((res) => {
        setOnfidoInitiateLoading(false);
        initiateKyc(res.data.sdk_data.token, res.data.workflow_data.id);
      })
      .catch((err) => {
        setOnfidoInitiateLoading(false);
      });
  };

  const onfidoRef = useRef(null);

  const initiateKyc = (token, workflowRunId) => {
    console.log("Onfido Studio Token:", token);
    console.log("Workflow Run ID:", workflowRunId);

    setOnfidoInitiateLoading(true);
    setOnfidoStarted(true);

    let onfidoInstance;

    onfidoInstance = Onfido.init({
      token,
      containerId: "onfido-mount",
      language: localStorage.getItem("i18nextLng") === "es" ? "es" : "en_US",
      workflowRunId,
      theme: {
        name: "light",
        config: {
          colorBackgroundButtonPrimary: "#253837",
          colorBorderButtonPrimary: "#253837",
          colorBackgroundButtonPrimaryHover: "#a3eb64",
          colorBorderButtonPrimaryHover: "#a3eb64",
          colorBorderSurfaceModal: "transparent",
          colorBackgroundSurfaceModal: "transparent",
          colorBorderButtonPrimaryActive: "#a3eb64",
          colorBackgroundButtonPrimaryActive: "#a3eb64",
          colorBorderButtonPrimaryFocus: "#a3eb64",
          borderRadiusButton: "18px",
          colorBorderButtonSecondary: "#253837",
          colorBorderButtonSecondaryHover: "#a3eb64",
          colorBorderButtonSecondaryActive: "#a3eb64",
          colorBorderButtonSecondaryFocus: "#a3eb64",
          colorContentButtonSecondaryText: "#253837",
          colorBackgroundButtonSecondary: "transparent",
          colorBackgroundButtonSecondaryHover: "transparent",
          colorBorderDocTypeButtonHover: "#a3eb64",
          colorBackgroundButtonTertiaryHover: "#a3eb64",
          colorBackgroundDropdownItemHover: "#a3eb64",
          colorBackgroundDropdownItemActive: "#253837",
          colorContentLinkTextHover: "#a3eb64",
          colorBorderLinkUnderline: "#253837",
        },
      },
      ...(isGuarantor && {
        translations: {
          en_US: {
            welcome: {
              title: _t("verifyYourGuarantorsIdentity"),
            },
          },
          es: {
            welcome: {
              title: _t("verifyYourGuarantorsIdentity"),
            },
          },
        },
      }),
      onComplete: async (data) => {
        console.log("KYC completed successfully:", data);
        localStorage.removeItem("modal");

        setOnfidoInitiateLoading(false);
        setOnfidoStarted(false);

        try {
          await onfidoInstance?.tearDown();
        } catch (err) {
          console.warn("Error tearing down Onfido instance:", err);
        }

        setKycVerificationPopup(true);
        await saveOnfidoKycResponse(data);
        if (usedFor !== "tenantChecks") {
          loadUserTenant("tenant");
        }

        setTimeout(() => {
          if (usedFor === "popup") {
            setShowKycPopup(false);
          } else if (usedFor === "tenantChecks") {
            const _step = getNextStep();
            updateUserProfile({ id: user?._id, step: _step });
          } else {
            updateUserProfile({ step: 6, role: "tenant" });
            if (!isGuarantor) {
              trackClarityEvent("tenant_kyc_step_ended");
              setTimeout(() => {
                trackClarityEvent("tenant_signup_completed");
                trackClarityEvent("tenant_bank_connect_step_started");
              }, constants.CLARITY_DELAY);
            }
          }
        }, 2000);
      },
      onError: async (err) => {
        console.error("Onfido SDK Error:", err);
        toast.error(
          _t("kycVerificationFailed", "Used as an error message on KYC screen")
        );
        setOnfidoInitiateLoading(false);
        setOnfidoStarted(false);

        try {
          await onfidoInstance?.tearDown();
        } catch (err) {
          console.warn("Error tearing down Onfido instance:", err);
        }
        trackClarityEvent("tenant_kyc_failed");
      },
      onUserExit: async (userExitCode) => {
        console.log("User exited KYC:", userExitCode);
        setOnfidoInitiateLoading(false);
        setOnfidoStarted(false);

        try {
          if (onfidoInstance?.tearDown) {
            await onfidoInstance.tearDown();
          }
        } catch (err) {
          console.warn("Error tearing down Onfido instance:", err);
        }
      },
    });
  };

  const saveOnfidoKycResponse = async (kycData) => {
    await onfidoKyc(
      user?._id,
      kycData,
      isGuarantor
        ? "?guarantor=true"
        : usedFor === "tenantChecks"
          ? "?llCreated=true"
          : ""
    );
  };
  // ----------------------------- onfido functionality -----------------------------

  console.log(addedFiles, "checkAddedfilesHere");

  return (
    <>
      {onfidoStarted && (
        <div className="onfido-overlay">
          <div className="onfido-wrapper">
            <div
              className="cursor-pointer"
              onClick={async () => {
                try {
                  await onfidoRef.current?.tearDown();
                  setOnfidoStarted(false);
                } catch (err) {
                  console.warn("Error tearing down Onfido:", err);
                }
                setOnfidoStarted(false);
                setOnfidoInitiateLoading(false);
              }}
            >
              <img
                src={Cancelimg}
                alt="icon"
                className="canceicon topRightIcon"
              />
            </div>

            {/* Onfido renders inside this container */}
            <div id="onfido-mount"></div>
          </div>
        </div>
      )}
      <div
        className={`approvedModal popup-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 wrapper-modal modal-fixed-screen ${
          isGuarantor ? "guarantorBg" : ""
        }`}
        style={{ zIndex: 99 }}
      >
        {!onfidoStarted && (
          <div
            className={`popup-content2 position-relative popup-content bg-white required-doc-popup onfiddoModal`}
            onClick={isGuarantor ? (e) => e.stopPropagation() : null}
          >
            {(isGuarantor || usedFor === "popup") && (
              <div
                className="cursor-pointer"
                onClick={
                  usedFor === "popup"
                    ? () => setShowKycPopup(false)
                    : () => navigate("/home")
                }
              >
                <img
                  src={Cancelimg}
                  alt="icon"
                  className="canceicon topRightIcon"
                />
              </div>
            )}
            <div className="row relativeposition">
              <div className="col-12 d-flex gap-4 align-items-center justify-content-center heading-section">
                <div className="authenticateid-header text-center">
                  <h4>
                    {isGuarantor
                      ? _t(
                          "authenticateTheGuarantorsId",
                          "Text used in authenticate scrren at onboarding process"
                        )
                      : _t(
                          "authenticateYourId",
                          "Text used in authenticate scrren at onboarding process"
                        )}{" "}
                    <span>
                      (
                      {_t(
                        "dniNieOrPassport",
                        "Text used in authenticate scrren at onboarding process"
                      )}
                      )
                    </span>
                  </h4>
                  <p>
                    {isGuarantor
                      ? _t(
                          "forYourGuarantorsVerificationAndToCompleteTheirProfilePleaseUploadTheirIdentityDocument",
                          "Text used in authenticate scrren at onboarding process"
                        )
                      : _t(
                          "forYourAccountSecurityAndStrongProfilePleaseCompleteIdentityVerfification",
                          "Text used in authenticate scrren at onboarding process"
                        )}
                  </p>
                </div>
              </div>
              {usedFor !== "popup" && (
                <div className="getback-btn">
                  <img
                    src={Backimg}
                    alt="icon"
                    className="canceicon"
                    onClick={
                      addedFiles?.length > 0
                        ? () => setAddedFiles([])
                        : formHandler.prev
                    }
                  />
                </div>
              )}
            </div>
            <div className="dg_paddingdiv">
              <div className="row uploaderWrapper justify-content-center">
                {/* <div
                  className={`col-12 col-md-6`}
                  onClick={handleClick}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                    accept="application/pdf, image/jpeg, image/png, image/jpg"
                    multiple
                  />
                  <div
                    className="dgboxsetup h-100 position-relative"
                    style={{
                      backgroundColor: isDragging ? "#A3EB64" : "transparent",
                      border:
                        addedFiles?.length > 0 ? "0.069vw solid #A3EB64" : "",
                      transition: "background-color 0.2s ease",
                    }}
                  >
                    {docUploadLoading ? (
                      // <Loader waiting_text={_t("uploadingDocuments")} />
                      <div className="defaultSkorLoaderParent">
                        <img
                          src={SkorDefaultLoader}
                          className="defaultSkorLoader"
                          alt="Example"
                        />
                      </div>
                    ) : (
                      <>
                        <div className="d-flex align-items-center justify-content-between">
                          <img
                            className="userimagesetup"
                            src={UserImg}
                            alt="img"
                          />
                          {addedFiles.length > 0 && (
                            <button
                              className="addMoreFilesButton d-flex align-items-center justify-content-center"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClick();
                              }}
                            >
                              <img src="/images/plusIconGreen.svg" />
                              {_t("addMoreFiles")}
                            </button>
                          )}
                        </div>
                        <h6>
                          {_t(
                            "dragAndDrogIdentityDocument",
                            "Text used in authenticate scrren at onboarding process"
                          )}
                        </h6>
                        {addedFiles?.length == 0 ? (
                          <>
                            <h5 className="">
                              {_t(
                                "uploadAValidGovernmentIdWithPhotoForEgDniNieOfSpainOrInCaseYouDontHaveItOrForeignUploadPassport",
                                "Text used in authenticate id scrren at onboarding process"
                              )}
                            </h5>
                            <p className="">
                              {_t(
                                "ensureThePhotoIsClearAndVisibleAndSizeOfTheFileMustBeLessThan2MB",
                                "Text used in authenticate id scrren at onboarding process"
                              )}
                            </p>
                          </>
                        ) : (
                          <div className="d-flex flex-column documentListWrapper">
                            {addedFiles?.map((files) => (
                              <div className="d-flex align-items-start gap-2">
                                <img
                                  className="docoment-img2"
                                  src={docIcon}
                                  alt="icon"
                                />
                                <div>
                                  <p className="staticdata_div custom-mb-2">
                                    {files?.name ?? ""}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {addedFiles?.length == 0 ? (
                          <button
                            type="button"
                            className="btnNext btnnextpre uploadButton position-relative w-100 uploadbtn uploadButtonMobile"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClick();
                            }}
                          >
                            {_t("upload")}
                            <img
                              className="doc_Uploadimg"
                              src={Uploadimg}
                              alt="img"
                            />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btnNext w-100 custom-mt-2 uploadbtn nextButton nextButtonUploadFiles"
                            onClick={(e) => {
                              e.stopPropagation();
                              submitVerification();
                            }}
                          >
                            {_t("next")}
                            <img
                              className="doc_Uploadimg"
                              src={Arrow}
                              alt="img"
                            />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div> */}
                <div
                  className="col-md-10 col-12 dgboxsetup-wrapper"
                  style={{ opacity: addedFiles?.length > 0 ? "0.5" : "" }}
                >
                  {onfidoInitiateLoading ? (
                    <div className="dgboxsetup authenticateModal h-100 dgboxsetup2">
                      {/* <Loader /> */}
                      <div className="defaultSkorLoaderParent">
                        <img
                          src={SkorDefaultLoader}
                          className="defaultSkorLoader"
                          alt="Example"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="dgboxsetup dgboxsetup2 h-100">
                      <img
                        className="userimagesetup"
                        src={UserImg2}
                        alt="img"
                      />
                      <h6>{_t("digitalKYC")}</h6>
                      <h5 className="">
                        {isGuarantor
                          ? _t(
                              "haveYourGuarantorsOriginalPhysicalIdReadyOrAPictureOfItFrontAndBack",
                              "Text used in authenticate id scrren at onboarding process"
                            )
                          : _t(
                              "getYourOriginalPhysicalIdWithYou",
                              "Text used in authenticate id scrren at onboarding process"
                            )}
                      </h5>
                      <p className=" selectCountryText">
                        {_t(
                          "selectYourRespectiveCountryTakeAPictureAndUploadYourDocument",
                          "Text used in authenticate id scrren at onboarding process"
                        )}
                      </p>
                      <button
                        type="button"
                        className={`btnNext btnnextpre continueButton position-relative w-100 uploadbtn continueButtonMobile`}
                        disabled={
                          onfidoInitiateLoading || addedFiles?.length > 0
                        }
                        onClick={initiateOnfido}
                      >
                        {_t("continue")}
                        <img className="doc_Uploadimg" src={Arrow} alt="img" />
                      </button>
                      {showOnfidoWatingLoading && (
                        <p style={{ color: "gray" }}>
                          {_t("pleaseWaitWhileWeAreFetchingYourKycDetails")}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center lockIconWrapper justify-content-center gap-1">
              <img className="lockIcon" src={lockIcon} alt="img" />
              <p className="lock-heading">
                {_t(
                  "yourDataIsEncrypted",
                  "Text used in authenticate id scrren at onboarding process"
                )}
              </p>
            </div>
          </div>
        )}
      </div>
      <KycVerificationPopup
        showPopup={kycVerificationPopup}
        setKycVerificationPopup={setKycVerificationPopup}
        loadUser={loadUser}
        isGuarantor={isGuarantor}
      />
      <KycDocLoader isGuarantor={isGuarantor} showPopup={showKycLoading} />
      <ProcessFailedModal
        showPopup={failedKyc}
        isGuarantor={isGuarantor}
        heading={_t("kycVerificationFailed")}
        subheading={_t("yourKycVerificationHasBeenFailed")}
        closeModalState={setFailedKyc}
      />
    </>
  );
};

export default AuthenticateYourId;
